import React, { useMemo } from "react";
import "./reactTable.css";

import {
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";

import {
    ThemeProvider,
    createTheme,
} from "@mui/material/styles";


const ReactTable = ({ columns = [], data = [] }) => {

    const firstColumnId = columns[0]?.id ?? columns[0]?.accessorKey;

    /*
     * We intentionally keep MUI's base theme LIGHT here.
     *
     * Your application's ThemeContext already adds/removes:
     * body.dark-theme
     *
     * reactTable.css handles the complete dark/light appearance.
     *
     * This means:
     * - no useContext()
     * - no useThemeMode()
     * - no transparent colors
     * - no CSS var() dependency
     */

    const tableTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: "light",

                    primary: {
                        main: "#2275FC",
                    },

                    background: {
                        default: "#F2F7FB",
                        paper: "#FFFFFF",
                    },

                    text: {
                        primary: "#0F172A",
                        secondary: "#475569",
                    },

                    divider: "#E2E8F0",
                },

                components: {

                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                backgroundImage: "none",
                                boxShadow: "none",
                            },
                        },
                    },

                    MuiToolbar: {
                        styleOverrides: {
                            root: {
                                backgroundColor: "#FFFFFF",
                                color: "#334155",
                            },
                        },
                    },

                    MuiTableCell: {
                        styleOverrides: {
                            root: {
                                borderColor: "#E2E8F0",
                            },

                            head: {
                                backgroundColor: "#F1F5F9",
                                color: "#0F172A",
                                fontWeight: 700,
                            },

                            body: {
                                color: "#334155",
                            },
                        },
                    },

                    MuiIconButton: {
                        styleOverrides: {
                            root: {
                                color: "#475569",
                            },
                        },
                    },

                    MuiTablePagination: {
                        styleOverrides: {
                            root: {
                                color: "#334155",
                            },

                            toolbar: {
                                backgroundColor: "#FFFFFF",
                            },

                            selectLabel: {
                                color: "#64748B",
                            },

                            displayedRows: {
                                color: "#64748B",
                            },

                            select: {
                                color: "#334155",
                            },
                        },
                    },

                    MuiInputBase: {
                        styleOverrides: {
                            root: {
                                color: "#0F172A",
                            },
                        },
                    },

                    MuiOutlinedInput: {
                        styleOverrides: {

                            root: {
                                backgroundColor: "#F8FAFC",
                                borderRadius: 8,

                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#E2E8F0",
                                },

                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#CBD5E1",
                                },

                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#2275FC",
                                },
                            },

                            input: {
                                color: "#0F172A",
                            },
                        },
                    },

                    MuiSelect: {
                        styleOverrides: {
                            icon: {
                                color: "#475569",
                            },
                        },
                    },

                    MuiMenuItem: {
                        styleOverrides: {
                            root: {
                                color: "#0F172A",

                                "&:hover": {
                                    backgroundColor: "#F1F5F9",
                                },
                            },
                        },
                    },
                },
            }),
        []
    );


    const table = useMaterialReactTable({
        columns,
        data,
        layoutMode: "grid",
        /*
         * BASIC TABLE SETTINGS
         */
        enableFullScreenToggle: false,
        enableColumnFilters: false,
        enableColumnActions: false,

        enableSorting: true,
        enablePagination: true,
        enableStickyHeader: true,

        /*
         * SEARCH
         */
        // enableGlobalFilter: true,
        // positionGlobalFilter: "left",

        /*
         * PAGINATION
         */
        paginationDisplayMode: "pages",

        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 10,
            },

            density: "compact",

            // showGlobalFilter: true,
        },


        /*
         * TABLE
         */
        muiTableProps: {
            sx: {
                borderCollapse: "separate",
                borderSpacing: 0,
                tableLayout: "fixed",
                width: "100%",
            },
        },


        /*
         * MAIN PAPER
         */
        muiTablePaperProps: {
            elevation: 0,

            sx: {
                backgroundColor: "#FFFFFF",
                color: "#334155",
                borderRadius: "10px",
                overflow: "hidden",
            },
        },


        /*
         * TABLE CONTAINER
         */
        muiTableContainerProps: {
            sx: {
                backgroundColor: "#FFFFFF",

                maxHeight: "min(64vh, 680px)",

                overflowX: "auto",
                overflowY: "auto",

                scrollbarWidth: "thin",
            },
        },


        /*
         * TOP TOOLBAR
         */
        muiTopToolbarProps: {
    sx: {
        minHeight: "42px",
        height: "42px",

        padding: "4px 8px",

        display: "flex",
        alignItems: "center",

        "& > *": {
            backgroundColor: "inherit",
        },

        "& .MuiBox-root": {
            backgroundColor: "inherit",
            color: "inherit",
        },
    },
},

// muiSearchTextFieldProps: {
//     placeholder: "Search...",
//     variant: "outlined",
//     size: "small",

//     sx: {
//         width: "190px",
//         minWidth: "190px",

//         backgroundColor: "#1f2a3d",

//         borderRadius: "6px",

//         "& .MuiInputBase-root": {
//             width: "190px",

//             height: "30px",
//             minHeight: "30px",

//             padding: "0",

//             backgroundColor: "#1f2a3d !important",

//             color: "#f8fafc",

//             borderRadius: "6px",
//         },

//         "& .MuiOutlinedInput-root": {
//             height: "30px",
//             minHeight: "30px",

//             backgroundColor: "#1f2a3d !important",

//             borderRadius: "6px",
//         },

//         "& .MuiOutlinedInput-input": {
//             height: "30px",
//             minHeight: "30px",

//             boxSizing: "border-box",

//             padding: "4px 8px",

//             fontSize: "11px",

//             color: "#f8fafc !important",

//             backgroundColor: "#1f2a3d !important",
//         },

//         "& .MuiOutlinedInput-notchedOutline": {
//             borderColor: "#324055 !important",
//         },

//         "&:hover .MuiOutlinedInput-notchedOutline": {
//             borderColor: "#475569 !important",
//         },

//         "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
//             borderColor: "#60a5fa !important",
//         },

//         "& .MuiInputAdornment-root": {
//             color: "#94a3b8 !important",
//         },

//         "& input::placeholder": {
//             color: "#94a3b8 !important",
//             opacity: 1,
//         },
//     },
// },


        /*
         * BOTTOM TOOLBAR / PAGINATION
         */
        muiBottomToolbarProps: {
    sx: {
        minHeight: "48px",
        height: "48px",

        padding: "2px 8px",

        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",

        "& > *": {
            backgroundColor: "inherit",
        },

        "& .MuiBox-root": {
            backgroundColor: "inherit",
            color: "inherit",
        },
    },
},


        /*
         * HEADER
         */
        muiTableHeadCellProps: {
            sx: {
                backgroundColor: "#F1F5F9",

                color: "#0F172A",

                borderBottom: "1px solid #E2E8F0",

                fontSize: "12px",

                fontWeight: 700,

                lineHeight: 1.35,

                letterSpacing: "0.2px",

                padding: "10px 12px",

                whiteSpace: "normal",

                overflowWrap: "anywhere",

                boxShadow: "none",

                textAlign: "center",

                "& .MuiTableSortLabel-root": {
                    color: "#0F172A !important",
                    justifyContent: "center",
                },

                "& .MuiTableSortLabel-icon": {
                    color: "#64748B !important",
                    
                    opacity: 1,
                },

                "& .MuiIconButton-root": {
                    color: "#64748B",
                },
            },
        },


        /*
         * BODY ROW
         */
        muiTableBodyRowProps: ({ row }) => {

            const backgroundColor =
                row.index % 2 === 0
                    ? "#FFFFFF"
                    : "#F8FAFC";

            return {
                sx: {

                    "& > td": {
                        backgroundColor,
                    },

                    "&:hover > td": {
                        backgroundColor: "#EEF4FF",
                    },
                },
            };
        },


        /*
         * BODY CELL
         */
        muiTableBodyCellProps: ({ cell }) => {

            const isFirstColumn =
                cell.column.id === firstColumnId;

            return {
                sx: {

                    backgroundColor: "inherit",

                    color: "#334155",

                    borderBottom: "1px solid #E2E8F0",

                    fontSize: "13px",

                    lineHeight: 1.4,

                    padding: "9px 12px",

                    whiteSpace: "normal",

                    overflowWrap: "anywhere",

                    wordBreak: "break-word",

                    boxShadow: "none",

                    transition:
                        "background-color 0.15s ease",

                    ...(isFirstColumn && {
                        fontWeight: 600,
                        color: "#0F172A",
                    }),
                },
            };
        },


        /*
         * BODY
         */
        muiTableBodyProps: {
            sx: {

                "& tr:last-of-type td": {
                    borderBottom: "0",
                },
            },
        },

        /*
         * PAGINATION
         */
        muiPaginationProps: {

            color: "primary",

            shape: "rounded",

            variant: "outlined",

            rowsPerPageOptions: [
                10,
                25,
                50,
            ],

            showFirstButton: true,

            showLastButton: true,
        },


        /*
         * HEADER CORNERS
         */
        muiTableHeadProps: {
            sx: {

                "& th:first-of-type": {
                    borderTopLeftRadius: "10px",
                },

                "& th:last-of-type": {
                    borderTopRightRadius: "10px",
                },
            },
        },
    });


    return (
        <ThemeProvider theme={tableTheme}>

            <div className="data_table">

                <MaterialReactTable
                    table={table}
                />

            </div>

        </ThemeProvider>
    );
};


export default ReactTable;